/**
 * Karl — Welldora KI-Kundenberater Widget
 *
 * Einbindung: dieses Script per <script src="..."></script> vor dem schließenden
 * </body>-Tag im Shopify-Theme laden (z.B. via theme.liquid oder als eigener
 * App-Embed-Block). Kein Framework, keine Abhängigkeiten, kein Build-Schritt.
 *
 * WICHTIG vor Go-Live: WORKER_URL unten auf die echte Cloudflare-Worker-URL setzen
 * (siehe Setup-Anleitung.md).
 *
 * Karl-Maskottchen: Optionales window.KARL_ASSETS = { avatar: "...", typing: "..." }
 * vor diesem Script setzen (siehe layout/theme.liquid), um das Faultier-Icon statt
 * des Emoji-Platzhalters zu verwenden. Ohne KARL_ASSETS fällt das Widget automatisch
 * auf den Emoji-Look zurück — bricht also nichts, falls die Bilder mal fehlen.
 */
(function () {
  "use strict";

  // ---- Konfiguration ----
  const WORKER_URL = "https://karl-welldora.max-ernst.workers.dev"; // Deployed 14.09.2026
  const WIDGET_NAME = "Karl";
  const WELCOME_MESSAGE =
    "Hoi, ich bin Karl 👋 Dein KI-Schlafberater bei Welldora — ein KI-gestützter Assistent, der dich rund um bewusste, hochwertige Bettartikel berät. Wir stehen für Qualität und Transparenz ohne Kompromisse. Frag mich einfach drauflos, zu Produkten, Schlaf-Tipps, Versand oder Rückgabe!";
  const STORAGE_KEY = "welldora_karl_history";
  const INTRO_KEY = "welldora_karl_intro_shown";

  // Maskottchen-Bilder (Faultier, Welldora-Branding — siehe 07 Anhänge/Karl Maskottchen)
  const ASSETS = window.KARL_ASSETS || {};
  const AVATAR_URL = ASSETS.avatar || null;
  const TYPING_URL = ASSETS.typing || null;
  // Lauf-Posen (Ganzkörper-Karl mit Beinen) für den Intro-Auftritt — werden im Wechsel
  // durchgeschaltet, sodass Karl beim Reinlaufen tatsächlich "läuft". Fehlen sie, fällt
  // der Auftritt auf den runden Avatar zurück.
  const WALK_FRAMES = Array.isArray(ASSETS.walk) ? ASSETS.walk.filter(Boolean) : [];

  // ---- Styles (scoped über Präfix, kein CSS-Reset nötig) ----
  const style = document.createElement("style");
  style.textContent = `
    @keyframes karl-pulse-ring {
      0%, 100% { box-shadow: 0 4px 16px rgba(0,0,0,0.2), 0 0 0 0 rgba(42,107,43,0.35); }
      50% { box-shadow: 0 4px 16px rgba(0,0,0,0.2), 0 0 0 9px rgba(42,107,43,0); }
    }
    #karl-widget-bubble {
      position: fixed; bottom: 20px; right: 20px; z-index: 999999;
      width: ${AVATAR_URL ? "78px" : "60px"}; height: ${AVATAR_URL ? "78px" : "60px"};
      border-radius: 50%;
      background: ${AVATAR_URL ? `#E6DED6 url('${AVATAR_URL}') center / cover no-repeat` : "#2c2c2c"};
      color: #fff; border: none; cursor: pointer;
      box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      display: flex; align-items: center; justify-content: center;
      font-size: 26px; transition: transform 0.15s ease;
      animation: karl-pulse-ring 2.6s ease-in-out infinite;
    }
    #karl-widget-bubble.karl-no-pulse { animation: none; }
    #karl-widget-bubble:hover { transform: scale(1.06); }
    #karl-widget-panel {
      position: fixed; bottom: 92px; right: 20px; z-index: 999999;
      width: 360px; max-width: calc(100vw - 32px);
      height: 520px; max-height: calc(100vh - 140px);
      background: #fff; border-radius: 16px; box-shadow: 0 8px 32px rgba(0,0,0,0.25);
      display: none; flex-direction: column; overflow: hidden;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }
    #karl-widget-panel.open { display: flex; }
    #karl-widget-header {
      background: #2c2c2c; color: #fff; padding: 14px 16px;
      display: flex; align-items: center; gap: 10px; justify-content: space-between;
      font-size: 15px; font-weight: 600;
    }
    #karl-widget-header-title { display: flex; align-items: center; gap: 10px; }
    #karl-widget-header-avatar {
      width: 30px; height: 30px; border-radius: 50%; background: #E6DED6;
      background-size: cover; background-position: center; flex-shrink: 0;
    }
    #karl-widget-close { background: none; border: none; color: #fff; font-size: 20px; cursor: pointer; line-height: 1; }
    #karl-widget-messages { flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 10px; }
    .karl-msg-row { display: flex; align-items: flex-end; gap: 8px; max-width: 88%; }
    .karl-msg-row.user { align-self: flex-end; flex-direction: row-reverse; }
    .karl-msg-row.assistant, .karl-msg-row.loading { align-self: flex-start; }
    .karl-msg-avatar {
      width: 24px; height: 24px; border-radius: 50%; background: #E6DED6;
      background-size: cover; background-position: center; flex-shrink: 0;
    }
    .karl-msg { padding: 9px 13px; border-radius: 14px; font-size: 14px; line-height: 1.4; white-space: pre-wrap; word-wrap: break-word; }
    .karl-msg.assistant { background: #f0f0ee; color: #222; border-bottom-left-radius: 4px; }
    .karl-msg.user { background: #2c2c2c; color: #fff; border-bottom-right-radius: 4px; }
    .karl-msg a { color: #2a6b2b; font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }
    .karl-msg a:hover { text-decoration: none; }
    .karl-msg.user a { color: #fff; }
    .karl-msg strong { font-weight: 700; }
    .karl-msg.loading { background: #f0f0ee; color: #999; display: flex; align-items: center; gap: 6px; }
    .karl-typing-img { width: 26px; height: 26px; border-radius: 50%; animation: karl-typing-breathe 1.4s ease-in-out infinite; }
    @keyframes karl-typing-breathe { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
    .karl-feedback { display: flex; gap: 4px; margin-top: 3px; margin-left: 32px; }
    .karl-feedback-btn {
      background: none; border: none; cursor: pointer; font-size: 13px;
      opacity: 0.45; padding: 2px 4px; border-radius: 6px; line-height: 1;
      transition: opacity 0.15s ease, background 0.15s ease;
    }
    .karl-feedback-btn:hover { opacity: 0.85; background: #f0f0ee; }
    .karl-feedback-btn.karl-feedback-active { opacity: 1; }
    .karl-feedback-btn:disabled { cursor: default; }
    .karl-feedback-thanks { font-size: 12px; color: #999; margin-left: 32px; margin-top: 3px; }
    #karl-widget-inputrow { display: flex; border-top: 1px solid #eee; padding: 10px; gap: 8px; }
    #karl-widget-input {
      flex: 1; border: 1px solid #ddd; border-radius: 20px; padding: 9px 14px;
      font-size: 14px; outline: none; font-family: inherit;
    }
    #karl-widget-input:focus { border-color: #2c2c2c; }
    #karl-widget-send {
      background: #2c2c2c; color: #fff; border: none; border-radius: 50%;
      width: 38px; height: 38px; cursor: pointer; font-size: 16px; flex-shrink: 0;
    }
    #karl-widget-send:disabled { opacity: 0.5; cursor: not-allowed; }
    @media (max-width: 480px) {
      #karl-widget-panel { width: calc(100vw - 24px); right: 12px; bottom: 84px; height: calc(100vh - 160px); }
      #karl-widget-bubble { right: 16px; bottom: 16px; }
    }

    /* ---- Intro-Auftritt: Karl läuft (mit Beinen) einmal pro Session über den Bildschirm und dockt unten rechts an ---- */
    #karl-intro { position: fixed; inset: 0; z-index: 1000000; pointer-events: none; overflow: hidden; }
    #karl-intro-walker {
      position: fixed; bottom: 14px; right: 20px; height: 136px; width: auto;
      will-change: transform; animation: karl-walk-across 7s cubic-bezier(0.15, 0, 0.25, 1) forwards;
    }
    #karl-intro-sprite {
      height: 100%; width: auto; display: block;
      filter: drop-shadow(0 10px 7px rgba(0,0,0,0.18));
    }
    #karl-intro-say {
      position: absolute; bottom: 100%; right: 4px; margin-bottom: 4px; white-space: nowrap;
      background: #fff; color: #2c2c2c; font-weight: 600; font-size: 15px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      padding: 10px 16px; border-radius: 16px 16px 4px 16px;
      box-shadow: 0 6px 20px rgba(0,0,0,0.18); opacity: 0;
      transform: translateY(8px) scale(0.9); transform-origin: bottom right;
    }
    #karl-intro-say.show { animation: karl-say-pop 0.4s ease forwards; }
    #karl-intro-walker.dock {
      opacity: 0; transform: translateY(8px) scale(0.9); transform-origin: bottom right;
      transition: opacity 0.45s ease, transform 0.45s ease;
    }

    @keyframes karl-walk-across {
      0%   { transform: translateX(calc(-100vw - 220px)); }
      100% { transform: translateX(0); }
    }
    @keyframes karl-say-pop {
      0%   { opacity: 0; transform: translateY(8px) scale(0.9); }
      60%  { opacity: 1; transform: translateY(-2px) scale(1.04); }
      100% { opacity: 1; transform: translateY(0) scale(1); }
    }
    @media (max-width: 480px) {
      #karl-intro-walker { height: 112px; right: 16px; }
      #karl-intro-say { font-size: 14px; }
    }
    @media (prefers-reduced-motion: reduce) {
      #karl-widget-bubble { animation: none; }
    }
  `;
  document.head.appendChild(style);

  // ---- DOM aufbauen ----
  const bubble = document.createElement("button");
  bubble.id = "karl-widget-bubble";
  bubble.setAttribute("aria-label", "Chat mit " + WIDGET_NAME + " öffnen");
  if (!AVATAR_URL) {
    bubble.textContent = "💬"; // Fallback, falls KARL_ASSETS nicht gesetzt ist
  }

  const panel = document.createElement("div");
  panel.id = "karl-widget-panel";
  panel.innerHTML = `
    <div id="karl-widget-header">
      <div id="karl-widget-header-title">
        ${AVATAR_URL ? `<div id="karl-widget-header-avatar" style="background-image:url('${AVATAR_URL}')"></div>` : ""}
        <span>${WIDGET_NAME} · KI-Schlafberater</span>
      </div>
      <button id="karl-widget-close" aria-label="Chat schließen">×</button>
    </div>
    <div id="karl-widget-messages"></div>
    <div id="karl-widget-inputrow">
      <input id="karl-widget-input" type="text" placeholder="Deine Frage an ${WIDGET_NAME} ..." autocomplete="off" />
      <button id="karl-widget-send" aria-label="Senden">➤</button>
    </div>
  `;

  document.body.appendChild(bubble);
  document.body.appendChild(panel);

  const messagesEl = panel.querySelector("#karl-widget-messages");
  const inputEl = panel.querySelector("#karl-widget-input");
  const sendBtn = panel.querySelector("#karl-widget-send");
  const closeBtn = panel.querySelector("#karl-widget-close");

  // ---- Verlauf (nur für die aktuelle Browser-Session, kein Server-seitiges Tracking) ----
  let history = [];
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) history = JSON.parse(saved);
  } catch (e) {
    history = [];
  }

  function saveHistory() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } catch (e) {
      /* Storage evtl. deaktiviert — kein Problem, Verlauf geht nur beim Reload verloren */
    }
  }

  // Escaped Text -> sicheres HTML mit anklickbaren Links + Fettschrift.
  // Karl antwortet in einfachem Markdown ([Text](URL), **fett**) — das Widget
  // hat das bisher als Rohtext angezeigt statt es darzustellen. escapeHtml()
  // läuft zuerst über den kompletten Text, danach werden nur die beiden
  // erlaubten Markdown-Muster in echte Tags umgewandelt (kein Risiko durch
  // eingeschleusten HTML-Code, auch nicht über Nutzereingaben).
  function escapeHtml(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function formatMessageHtml(text) {
    let safe = escapeHtml(text);
    // [Linktext](https://...) -> anklickbarer Link, öffnet in neuem Tab
    safe = safe.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    // **fett** -> <strong>
    safe = safe.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    return safe;
  }

  function renderMessage(role, text) {
    const row = document.createElement("div");
    row.className = "karl-msg-row " + role;

    if (role !== "user" && AVATAR_URL) {
      const avatar = document.createElement("div");
      avatar.className = "karl-msg-avatar";
      avatar.style.backgroundImage = `url('${AVATAR_URL}')`;
      row.appendChild(avatar);
    }

    const bubbleEl = document.createElement("div");
    bubbleEl.className = "karl-msg " + role;
    bubbleEl.innerHTML = formatMessageHtml(text);
    row.appendChild(bubbleEl);

    messagesEl.appendChild(row);
    messagesEl.scrollTop = messagesEl.scrollHeight;
    return row;
  }

  function renderLoading() {
    const row = document.createElement("div");
    row.className = "karl-msg-row loading";

    if (AVATAR_URL) {
      const avatar = document.createElement("div");
      avatar.className = "karl-msg-avatar";
      avatar.style.backgroundImage = `url('${AVATAR_URL}')`;
      row.appendChild(avatar);
    }

    const bubbleEl = document.createElement("div");
    bubbleEl.className = "karl-msg loading";
    if (TYPING_URL) {
      const img = document.createElement("img");
      img.src = TYPING_URL;
      img.alt = "Karl schreibt";
      img.className = "karl-typing-img";
      bubbleEl.appendChild(img);
    } else {
      bubbleEl.textContent = "…";
    }
    row.appendChild(bubbleEl);

    messagesEl.appendChild(row);
    messagesEl.scrollTop = messagesEl.scrollHeight;
    return row;
  }

  // ---- Feedback (👍/👎) — nur für Antworten aus der aktuellen Live-Session, nicht für
  // die Begrüßung oder aus sessionStorage wiederhergestellte Nachrichten (die haben keine messageId) ----
  function renderFeedback(afterRow, messageId) {
    if (!messageId) return;

    const row = document.createElement("div");
    row.className = "karl-feedback";
    row.innerHTML = `
      <button class="karl-feedback-btn" data-rating="up" aria-label="Hilfreiche Antwort">👍</button>
      <button class="karl-feedback-btn" data-rating="down" aria-label="Nicht hilfreiche Antwort">👎</button>
    `;
    afterRow.insertAdjacentElement("afterend", row);

    const buttons = row.querySelectorAll(".karl-feedback-btn");
    buttons.forEach((btn) => {
      btn.addEventListener("click", async () => {
        const rating = btn.getAttribute("data-rating");
        buttons.forEach((b) => (b.disabled = true));
        btn.classList.add("karl-feedback-active");

        const thanks = document.createElement("div");
        thanks.className = "karl-feedback-thanks";
        thanks.textContent = "Danke für dein Feedback!";
        row.replaceWith(thanks);

        try {
          await fetch(WORKER_URL.replace(/\/$/, "") + "/feedback", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ messageId, rating }),
          });
        } catch (e) {
          /* Feedback ist ein Nice-to-have — Fehler hier stören den Kunden nicht */
        }
      });
    });
  }

  // Beim ersten Öffnen: Verlauf wiederherstellen oder Begrüßung zeigen
  let initialized = false;
  function initMessages() {
    if (initialized) return;
    initialized = true;
    if (history.length === 0) {
      renderMessage("assistant", WELCOME_MESSAGE);
    } else {
      history.forEach((m) => renderMessage(m.role, m.content));
    }
  }

  bubble.addEventListener("click", () => {
    panel.classList.toggle("open");
    if (panel.classList.contains("open")) {
      initMessages();
      inputEl.focus();
      bubble.classList.add("karl-no-pulse"); // Pulsieren stoppt, sobald Karl einmal geöffnet wurde
    }
  });
  closeBtn.addEventListener("click", () => panel.classList.remove("open"));

  async function sendMessage() {
    const text = inputEl.value.trim();
    if (!text) return;

    renderMessage("user", text);
    inputEl.value = "";
    inputEl.disabled = true;
    sendBtn.disabled = true;

    const loadingRow = renderLoading();

    try {
      const response = await fetch(WORKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, history }),
      });

      if (!response.ok) throw new Error("HTTP " + response.status);
      const data = await response.json();

      loadingRow.remove();
      const replyRow = renderMessage("assistant", data.reply);
      renderFeedback(replyRow, data.messageId);

      history.push({ role: "user", content: text });
      history.push({ role: "assistant", content: data.reply });
      // Verlauf begrenzen, damit sessionStorage nicht unbegrenzt wächst
      if (history.length > 20) history = history.slice(-20);
      saveHistory();
    } catch (err) {
      loadingRow.remove();
      renderMessage(
        "assistant",
        "Entschuldige, gerade klappt die Verbindung nicht. Bitte versuch es später nochmal oder schreib uns direkt: schlafgut@welldora.org",
      );
    } finally {
      inputEl.disabled = false;
      sendBtn.disabled = false;
      inputEl.focus();
    }
  }

  sendBtn.addEventListener("click", sendMessage);
  inputEl.addEventListener("keydown", (e) => {
    if (e.key === "Enter") sendMessage();
  });

  // ---- Einmaliger Intro-Auftritt pro Browser-Session ----
  // Karl läuft als Faultier mit Beinen (zwei Lauf-Posen im Wechsel) von links über
  // den Bildschirm, winkt per Sprechblase, dockt unten rechts an, danach öffnet sich
  // das Chatfenster automatisch mit der Begrüßung. Fällt auf den runden Avatar zurück,
  // wenn keine Lauf-Frames gesetzt sind. Übersprungen bei reduzierter Bewegung, bereits
  // gezeigtem Auftritt in dieser Session oder vorhandenem Gesprächsverlauf.
  function playIntro() {
    const frames =
      WALK_FRAMES.length >= 2 ? WALK_FRAMES : AVATAR_URL ? [AVATAR_URL] : [];
    if (frames.length === 0) return;
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let shown = false;
    try {
      shown = sessionStorage.getItem(INTRO_KEY) === "1";
    } catch (e) {
      /* sessionStorage evtl. gesperrt — dann einfach Intro zeigen */
    }
    if (reduce || shown || history.length > 0) return;
    try {
      sessionStorage.setItem(INTRO_KEY, "1");
    } catch (e) {
      /* kein Problem */
    }

    // Frames vorladen, damit der Posen-Wechsel ruckelfrei startet
    frames.forEach((u) => {
      const pre = new Image();
      pre.src = u;
    });

    bubble.style.visibility = "hidden";

    const intro = document.createElement("div");
    intro.id = "karl-intro";
    const walker = document.createElement("div");
    walker.id = "karl-intro-walker";
    const sprite = document.createElement("img");
    sprite.id = "karl-intro-sprite";
    sprite.alt = "Karl läuft ins Bild";
    sprite.src = frames[0];
    const say = document.createElement("div");
    say.id = "karl-intro-say";
    say.textContent = "Hoi, ich bin Karl 👋";
    walker.appendChild(sprite);
    walker.appendChild(say);
    intro.appendChild(walker);
    document.body.appendChild(intro);

    // Lauf-Zyklus: Posen im Takt wechseln, solange Karl über den Screen läuft
    let fi = 0;
    const stepTimer =
      frames.length > 1
        ? setInterval(() => {
            fi = (fi + 1) % frames.length;
            sprite.src = frames[fi];
          }, 280)
        : null;

    const sayTimer = setTimeout(() => say.classList.add("show"), 5000);

    let done = false;
    function finish() {
      if (done) return;
      done = true;
      if (stepTimer) clearInterval(stepTimer);
      clearTimeout(sayTimer);
      if (intro.parentNode) intro.parentNode.removeChild(intro);
      bubble.style.visibility = "";
      bubble.classList.add("karl-no-pulse");
      panel.classList.add("open");
      initMessages();
    }

    walker.addEventListener("animationend", (e) => {
      if (e.animationName !== "karl-walk-across") return;
      // Angekommen: Beine anhalten, kurz winken lassen, dann sanft ins Chat-Icon übergehen
      if (stepTimer) clearInterval(stepTimer);
      sprite.src = frames[0];
      say.classList.add("show");
      setTimeout(() => walker.classList.add("dock"), 1100);
      setTimeout(finish, 1650);
    });

    // Sicherheitsnetz, falls ein animationend-Event mal ausbleibt
    setTimeout(finish, 11000);
  }

  playIntro();
})();
